import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AVAILABLE_LOCALES,
  LOCALE_STORAGE_KEY,
  i18n,
  type SupportedLocale,
} from '../i18n'

/**
 * useLocale — small wrapper around vue-i18n + localStorage persistence.
 *
 * Returns:
 *   - `locale` (readonly computed) — current locale code
 *   - `available` — supported locales with display label + flag
 *   - `setLocale(code)` — swap locale, persist, sync <html lang>,
 *     and (when wired) update PrimeVue's runtime locale.
 *
 * The composable is safe to call outside `setup()` (e.g. from router guards
 * or stores) because it falls back to `i18n.global` when `useI18n()` throws.
 *
 * PrimeVue locale wiring uses a closure-setter pattern: `main.ts` calls
 * `setPrimeVueLocaleApplier(fn)` once after `app.use(PrimeVue, ...)`, and
 * `setLocale()` invokes the applier when present. Tests don't need to mock
 * `usePrimeVue()` because the applier defaults to `null` outside boot.
 */
// Use the loose typing of the global composer so the type system doesn't
// constrain locales to whatever vue-i18n inferred at module init.
type LooseComposer = { locale: { value: string } }

type PrimeVueLocaleApplier = (code: SupportedLocale) => void

let primeVueLocaleApplier: PrimeVueLocaleApplier | null = null

/**
 * Register a callback that mutates the PrimeVue runtime config when the
 * locale changes. Wired from `main.ts` after PrimeVue is installed.
 *
 * Pass `null` to clear (useful for tests that re-mount the app).
 */
export function setPrimeVueLocaleApplier(fn: PrimeVueLocaleApplier | null): void {
  primeVueLocaleApplier = fn
}

export function useLocale() {
  // useI18n() throws when called outside a setup context — fall back to the
  // module-scope global instance so callers in stores/router can still use it.
  let scope: LooseComposer | null = null
  try {
    scope = useI18n() as unknown as LooseComposer
  } catch {
    scope = null
  }

  const locale = computed<SupportedLocale>(() => {
    const v = scope ? scope.locale.value : (i18n.global.locale.value as unknown as string)
    return v as SupportedLocale
  })

  function setLocale(code: SupportedLocale) {
    if (!AVAILABLE_LOCALES.some((l) => l.code === code)) return
    if (scope) {
      scope.locale.value = code
    }
    // Always update the global instance so module-scope `t()` callers see
    // the new locale even when invoked via a setup-bound composable.
    ;(i18n.global.locale as unknown as { value: string }).value = code

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, code)
      }
    } catch {
      // localStorage may be disabled (Safari private mode) — non-fatal.
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = code
    }

    // Propagate to PrimeVue's runtime locale when wired. The applier
    // mutates `usePrimeVue().config.locale` in place, which PrimeVue
    // components react to via the shared config reference.
    if (primeVueLocaleApplier) {
      try {
        primeVueLocaleApplier(code)
      } catch {
        // PrimeVue may not yet be installed (very early boot) — non-fatal.
      }
    }
  }

  return {
    locale,
    available: AVAILABLE_LOCALES,
    setLocale,
  }
}
